/* eslint-disable jsx-a11y/anchor-is-valid */
import './Login.css'
import {useEffect, useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import ReCAPTCHA from 'react-google-recaptcha'
import {authAPI} from '../../../api/client'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'
import {UserModel, AuthModel} from '../core/_models'
import {getUserByToken} from '../core/_requests'
import {resetAuthRedirectFlag} from '../../../services/service'
import md5 from 'crypto-js/md5'

const RECAPTCHA_SITE_KEY = '6LdodmEUAAAAAOJ96OqJQAWxog-lw6RkbGWvFBEP'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .min(3, 'Mínimo 3 caracteres')
    .max(255, 'Máximo 255 caracteres')
    .required('El usuario es requerido'),
  password: Yup.string()
    .min(2, 'Mínimo 2 caracteres')
    .max(50, 'Máximo 50 caracteres')
    .required('La contraseña es requerida'),
})

const initialValues = {
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const {saveAuth, saveUser} = useAuth()
  const navigate = useNavigate()

  // Resetear el flag de redirect al montar Login.
  // Esto permite que las requests de login funcionen después de un 401 automático.
  useEffect(() => {
    resetAuthRedirectFlag()
  }, [])

  const resolveHomeRoute = (perfil?: string | number) => {
    const perfilNum = Number(perfil)
    if (perfilNum === 1) {
      return '/home/admin'
    }
    if (perfilNum === 2) {
      return '/home/alumno'
    }
    if (perfilNum === 3) {
      return '/home/profesor'
    }
    if (perfilNum === 5) {
      return '/home/padre'
    }
    if (perfilNum === 7) {
      return '/home/multiperfil'
    }
    return '/home/preceptor'
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      if (!captchaToken) {
        setStatus('Por favor completá el captcha')
        return
      }
      setLoading(true)
      setStatus('')
      try {
        const passwordMd5 = md5(values.password).toString()
        const response = await authAPI.login(values.email, passwordMd5)
        const token = typeof response.data === 'string' ? response.data : response.data?.token
        if (!token) {
          setStatus('Error al iniciar sesión: Respuesta inválida del servidor')
          setSubmitting(false)
          setLoading(false)
          return
        }

        const auth: AuthModel = {
          api_token: token,
          refreshToken: undefined,
        }

        saveAuth(auth)

        const userResponse = await getUserByToken(token)
        const user: UserModel | undefined = userResponse.data
        if (!user) {
          setStatus('Error al iniciar sesión: No se pudieron cargar los datos del usuario')
          saveAuth(undefined)
          setSubmitting(false)
          setLoading(false)
          return
        }

        saveUser(user)
        navigate(resolveHomeRoute(user.perfil))
      } catch (error: any) {
        console.error(error)
        saveAuth(undefined)
        const errorMessage = error?.response?.data?.message || 'Credenciales incorrectas'
        setStatus(errorMessage)
        setSubmitting(false)
        setLoading(false)
        setCaptchaToken(null)
        recaptchaRef.current?.reset()
      }
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    formik.handleSubmit(e)
  }

  return (
      <form
        className='form w-100'
        onSubmit={handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* Logos */}
        <div className='text-center mb-8'>
          <img
            alt='TEACH Sistema Educativo'
            src={toAbsoluteUrl('/media/logos/teach-logo.png')}
            className='h-60px mb-4'
            style={{filter: 'brightness(0) saturate(100%) invert(22%) sepia(15%) saturate(600%) hue-rotate(90deg)'}}
          />
          <div>
            <img
              alt='Instituto Amancio Alcorta'
              src={toAbsoluteUrl('/media/logos/ialcorta-logo-text.png')}
              style={{
                maxWidth: '100%',
                filter: 'brightness(0) saturate(100%) invert(28%) sepia(12%) saturate(400%) hue-rotate(90deg)',
              }}
            />
          </div>
        </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Usuario</label>
        <input
          placeholder='usuario'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='text'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Contraseña</label>
        <div className='input-group'>
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
          >
            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='fs-base fw-semibold mb-8'>
        <Link to='/auth/forgot-password' style={{color: '#36553c'}}>
          Recuperar contraseña
        </Link>
      </div>
      {/* end::Wrapper */}

      {/* reCAPTCHA */}
      <div className='d-flex justify-content-center mb-6 recaptcha-wrapper'>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={(token: string | null) => setCaptchaToken(token)}
          onExpired={() => setCaptchaToken(null)}
        />
      </div>

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn text-white fw-bold'
          style={{backgroundColor: '#73bc7c'}}
          disabled={formik.isSubmitting || !formik.isValid || !captchaToken}
        >
          {!loading && <span className='indicator-label'>Ingresar</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Ingresando...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
      </form>
  )
}
